import { Drawer } from '@mui/material';
import getConfig from 'next/config';
import { useEffect, useMemo } from 'react';

import { OptimizelyFeatureFlags, TggMembershipType } from '@tgg/common-types';
import { dispatchEvent, EventKey } from '@tgg/services';
import { decimalToPoundsAndPence } from '@tgg/util';

import { useFlagsContext } from '../../contexts';
import { palette } from '../../theme';
import { Icon } from '../Icon';
import { PayPalMessage } from '../PinoProductOptions/PayPalMessage';
import { StyledPaper } from '../SideNavigation/SideNavigation.styled';
import { StickyNavigationButton } from '../StickyNavigationButton';

import {
    checkFixedProductSelected,
    checkNoJoiningFeePromo,
    getMappedTodayItems,
} from './GymBag.helpers';
import { useBasketData } from './GymBag.hook';
import {
    StyledBenefitItem,
    StyledBenefits,
    StyledContainer,
    StyledFindAGymIcon,
    StyledFindAGymLink,
    StyledIntroductionHeading,
    StyledNavigationButtonWrapper,
    StyledNavigationLink,
    StyledNavigationList,
    StyledParagraph,
    StyledPaymentIcons,
    StyledPayPalWrapper,
    StyledTotalCost,
    StyledTotalCostText,
    StyledHeading,
    StyledIntroductionParagraph,
    StyledGymBagHeader,
    StyledGymBagFooter,
} from './GymBag.styled';
import { GymBagProperties } from './GymBag.types';
import { GymBagTile } from './GymBagTile/GymBagTile';

const findAGymUrl = '/find-a-gym/';
const joinNowMembershipUrl = '/join-now/your-membership/';
const joinNowAddOnsUrl = '/join-now/add-ons/';
const joinNowNoFeeButtonText = 'join now with no joining fee';
const joinNowButtonText = 'join now';
const {
    publicRuntimeConfig: { PAYPAL_CLIENT_ID },
} = getConfig();

const handleGAEvent = (ctaName: string) =>
    void dispatchEvent(EventKey.CTA_BUTTON, {
        cta_name: ctaName,
        cta_position: 'gym bag',
        journey_type: 'returning user basket',
    });

export function GymBag({ open, onClose, domain = '' }: GymBagProperties) {
    const { basketData, isLoaded, expiredPromoCode } = useBasketData({
        domain,
    });

    useEffect(() => {
        if (open && basketData) {
            dispatchEvent(EventKey.VIEW_CART, {
                ecommerce: {
                    items: basketData.payments.today?.items.map(item => ({
                        item_name: item.label,
                        price: item.cost,
                        item_brand: basketData.branchName,
                        item_category: item.category,
                        item_variant: 'Create',
                        quantity: 1,
                    })),
                },
                journey_type: 'returning user basket',
            });
        }
    }, [basketData, open]);

    const {
        flags: {
            [OptimizelyFeatureFlags.PAYPAL_PAY_IN_3_GYM_BAG_IFRAME]: {
                enabled: isPaypalIframeEnabled,
                variationKey: isPaypalIframeEnabledVariationKey,
            },
        },
    } = useFlagsContext();

    const joinNowHref = useMemo(() => {
        if (!isLoaded || !basketData) return findAGymUrl;

        if (
            [
                TggMembershipType.UltimateMonthly,
                TggMembershipType.StandardMonthly,
                TggMembershipType.OffPeakMonthly,
            ].includes(
                basketData.basketItems?.product
                    ?.membershipType as TggMembershipType,
            )
        ) {
            if (
                basketData.basketItems &&
                basketData.basketItems.addOns.length >= 2
            )
                return joinNowMembershipUrl;

            return joinNowAddOnsUrl;
        }

        return joinNowMembershipUrl;
    }, [basketData, isLoaded]);

    const isPaypalIframeVisible =
        isPaypalIframeEnabled && isPaypalIframeEnabledVariationKey === 'on';
    const isJoiningFeePromoApplied = checkNoJoiningFeePromo(
        basketData?.payments.today?.items,
    );
    const tileItems = getMappedTodayItems(basketData);
    const isFixedProductSelected = checkFixedProductSelected(basketData);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            elevation={0}
            PaperProps={{ component: StyledPaper }}
        >
            <StyledGymBagHeader
                id="headerId"
                icon="gymBag"
                text="Your gym bag"
                onClose={onClose}
            />
            <StyledContainer>
                {isLoaded &&
                    (basketData ? (
                        <>
                            <div>
                                <StyledIntroductionHeading
                                    variant="h4"
                                    gutterBottom={false}
                                >
                                    let&apos;s finish what you started
                                </StyledIntroductionHeading>
                                <StyledIntroductionParagraph
                                    gutterBottom={false}
                                >
                                    Just a few more steps and you&apos;re good
                                    to go!
                                </StyledIntroductionParagraph>
                            </div>
                            <GymBagTile
                                items={tileItems}
                                title={basketData?.branchName ?? ''}
                                total={basketData.payments.today?.total}
                                promoCode={
                                    expiredPromoCode ??
                                    basketData.payments.promoCode
                                }
                                isPromoCodeExpired={!!expiredPromoCode}
                            />
                        </>
                    ) : (
                        <div>
                            <StyledParagraph>
                                Your gym bag is empty.
                            </StyledParagraph>
                            <StyledFindAGymLink
                                href={findAGymUrl}
                                onClick={() =>
                                    handleGAEvent('find a gym near you')
                                }
                            >
                                <StyledFindAGymIcon
                                    name="gymFinder"
                                    size={32}
                                    color={palette.common.white}
                                />
                                find a gym near you
                            </StyledFindAGymLink>
                            <StyledNavigationList>
                                <li>
                                    <StyledNavigationLink
                                        href="/gym-membership-deals-and-offers/"
                                        onClick={() =>
                                            handleGAEvent(
                                                'membership deals and offers',
                                            )
                                        }
                                    >
                                        membership deals and offers
                                    </StyledNavigationLink>
                                </li>
                                <li>
                                    <StyledNavigationLink
                                        href="/student-membership/"
                                        onClick={() =>
                                            handleGAEvent('student membership')
                                        }
                                    >
                                        student membership
                                    </StyledNavigationLink>
                                </li>
                            </StyledNavigationList>
                            <StyledHeading variant="h3">
                                all of our members benefit from:
                            </StyledHeading>
                            <StyledBenefits>
                                <StyledBenefitItem>
                                    <Icon name="dumbells" size={24} />
                                    Top Kit
                                </StyledBenefitItem>
                                <StyledBenefitItem>
                                    <Icon name="noContract" size={24} />
                                    No Contract
                                </StyledBenefitItem>
                                <StyledBenefitItem>
                                    <Icon name="cost" size={24} />
                                    Low Cost
                                </StyledBenefitItem>
                                <StyledBenefitItem>
                                    <Icon name="twentyFourHours" size={24} />
                                    24 Hour Opening
                                </StyledBenefitItem>
                            </StyledBenefits>
                        </div>
                    ))}
                <div>
                    <StyledHeading variant="h3">pay your way</StyledHeading>
                    <StyledPaymentIcons data-testid="paymentIcons">
                        <li>
                            <Icon
                                name="payPal"
                                aria-label="pay with PayPal"
                                size={60}
                                viewBox="0 0 61 16"
                            />
                        </li>
                        <li>
                            <Icon
                                name="directDebit"
                                aria-label="pay with Direct Debit"
                                viewBox="8.5 67.94 175.75 56.88"
                                size={46}
                            />
                        </li>
                        <li>
                            <Icon
                                name="visa"
                                aria-label="pay with visa"
                                rectangle
                                size={33}
                            />
                        </li>
                        <li>
                            <Icon
                                name="mastercard"
                                aria-label="pay with MasterCard"
                                rectangle
                                size={33}
                            />
                        </li>
                        <li>
                            <Icon
                                name="amex"
                                aria-label="pay with American Express"
                                rectangle
                                size={33}
                            />
                        </li>
                    </StyledPaymentIcons>
                </div>
            </StyledContainer>
            <StyledGymBagFooter>
                {isLoaded && basketData?.payments.today?.total && (
                    <StyledTotalCost>
                        <StyledTotalCostText>
                            payment due today
                        </StyledTotalCostText>
                        <StyledTotalCostText data-testid="total-cost">
                            {decimalToPoundsAndPence(
                                basketData?.payments.today?.total,
                            )}
                        </StyledTotalCostText>
                    </StyledTotalCost>
                )}
                {isLoaded &&
                    basketData?.payments.today?.total &&
                    isPaypalIframeVisible &&
                    isFixedProductSelected && (
                        <StyledPayPalWrapper>
                            <PayPalMessage
                                clientId={PAYPAL_CLIENT_ID}
                                amount={basketData.payments.today.total}
                                style={{ textAlign: 'left' }}
                                isItInTheGymBag
                            />
                        </StyledPayPalWrapper>
                    )}
                <StyledNavigationButtonWrapper>
                    <StickyNavigationButton
                        buttonText={
                            isJoiningFeePromoApplied
                                ? joinNowNoFeeButtonText
                                : joinNowButtonText
                        }
                        href={joinNowHref}
                        testId="join-now"
                        onClick={() => {
                            handleGAEvent(
                                isJoiningFeePromoApplied
                                    ? joinNowNoFeeButtonText
                                    : joinNowButtonText,
                            );
                        }}
                    />
                </StyledNavigationButtonWrapper>
            </StyledGymBagFooter>
        </Drawer>
    );
}
